import { Flex, Icon, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import { useSettingsState } from '~/atoms/SettingsState'
import { useUserState } from '~/atoms/UserState'
import { DropdownTextButton } from '~/components/shared/Dropdown/DropdownTextButton'
import { MenuItem, MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { useAnalytics } from '~/hooks/useAnalytics'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { ExtensionSelector } from '~/pages/WebPhone/ExtensionSelector'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'

import { ExtensionInstance } from '@mangovoice/types'

import { DependentSectionPanel, MenuItemHeader } from './Shared'

interface PhoneSettingToggleDependentSectionProps {
  icon?: SVGComponent
  isChecked: boolean
  toggleOnContent: React.ReactNode
  toggleOffContent?: React.ReactNode
}
export const PhoneSettingToggleDependentSection = ({
  icon,
  isChecked,
  toggleOnContent,
  toggleOffContent,
}: PhoneSettingToggleDependentSectionProps) => {
  const { t } = useTranslation()
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const { pbxExtensions, setCurrentUserExtension, pbx } = useUserState()
  const {
    confirmModal: [, setConfirmModalState],
  } = useModalState()
  const { setWebPhoneEnabled } = useSettingsState()
  const {
    state: {
      phoneCalls: {
        active: { activeCall },
      },
    },
  } = usePhoneEndpoint({})
  const handleWebPhoneActivate = () => {
    setWebPhoneEnabled(true)
  }

  //TODO put all ext stuff in separate component. shared with onboarding view
  const extItems = useMemo(() => {
    return (
      pbxExtensions?.map((ext) => {
        return {
          value: ext,
          content: (
            <MenuListItem
              primaryText={ext.name}
              rightContent={<ExtensionBadge extension={ext.number} />}
            />
          ),
          onSelect: (ext: ExtensionInstance) => {
            track(WebPhoneAnalyticsEvents.ExtSelected)
            setCurrentUserExtension(pbx?.id, ext)
            handleWebPhoneActivate()
          },
        } as MenuItem<ExtensionInstance>
      }) ?? []
    )
  }, [pbxExtensions])

  const checkCanActivatePhone = () => {
    if (pbxExtensions?.length === 1) {
      setCurrentUserExtension(pbx?.id, pbxExtensions[0])
      handleWebPhoneActivate()
    } else if (pbxExtensions?.length === 0) {
      setConfirmModalState({
        title: t('phoneSettingItems.confirmationModal.title'),
        message: (
          <Flex flexDirection="column" gap="1.5rem">
            <Text fontSize="1rem" fontWeight="400">
              {t('phoneSettingItems.confirmationModal.message1', {
                pbxName: pbx?.name,
              })}
            </Text>
            <Text fontSize="1rem" fontWeight="400">
              {t('phoneSettingItems.confirmationModal.message2')}
            </Text>
          </Flex>
        ),
        confirmText: t('phoneSettingItems.confirmationModal.confirmText'),
        closeAfterConfirm: true,
        isDestructive: false,
      })
    }
  }

  const shouldRoundBottomCorners = toggleOffContent === undefined && !isChecked

  return (
    <Flex
      direction="column"
      gap="16px"
      maxWidth="600px"
      width={{ base: '100%', xl: '50%' }}
    >
      {!isChecked ? (
        <MenuItemHeader
          roundBottomCorners={shouldRoundBottomCorners}
          padding="1rem"
          height="auto"
        >
          {icon && (
            <Icon as={icon} boxSize="24px" mr="10px" color="fg.default" />
          )}
          <DropdownTextButton
            items={extItems.length > 1 ? extItems : []}
            placeholder={t('phoneSettingItems.activateWebPhone')}
            onButtonClick={checkCanActivatePhone}
            menuOptions={{
              width: '23.9375rem',
            }}
            header={
              <Flex
                display="flex"
                padding="0.5rem 0.75rem 0rem 0.75rem"
                alignItems="center"
                gap="0.5rem"
                alignSelf="stretch"
              >
                <Text color="fg.default" fontSize="0.875rem" fontWeight="700">
                  {t('phoneSettingItems.selectExtension')}
                </Text>
              </Flex>
            }
          ></DropdownTextButton>
        </MenuItemHeader>
      ) : (
        <Flex
          display="flex"
          paddingX="16px"
          alignItems="center"
          gap="10px"
          alignSelf="stretch"
          width="100%"
          border="1px solid"
          borderColor="border.default"
          background="bg.default"
          borderRadius="8px"
        >
          <Text
            fontSize=".875rem"
            fontWeight="400"
            color="fg.default"
            width="100%"
          >
            {t('phoneSettingItems.yourExtension')}
          </Text>
          <ExtensionSelector hasActiveCall={!!activeCall} />
        </Flex>
      )}

      {isChecked && (
        <DependentSectionPanel>{toggleOnContent}</DependentSectionPanel>
      )}
      {!isChecked && toggleOffContent != undefined && (
        <DependentSectionPanel>{toggleOffContent}</DependentSectionPanel>
      )}
    </Flex>
  )
}
